// Merchant 页面业务 hooks。
// 合约读写通过 ../../chain/merchant/frontend，页面不直接调用合约。
// 钱包余额复用 ../../context/balance 的 useBalance。
// 输入、按钮、提示复用 useInput、useButtonText、useToast。

import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MerchantStairData, MigrateStair, SettleLegacyStair, CvcdExitData, ClaimCvcdExit, MerchantData, MerchantSwapData, MerchantStakeData, QuoteBuyUsd, QuoteRedeemUsd2,
    MerchantSponsor, EnsureMerchantAllowance, BuyMerchantIdentity, DepositUsd, WithdrawUsd2,
    InitialIdentityData, ClaimInitialIdentities, BuyUsd, RedeemUsd2, StakeFeeCvc, StakeManagerCvc, UnstakeSwapCvc, UnstakeFeeCvc,
    UnstakeManagerCvc, MerchantFeeData, UnstakeAllSwapCvc, UnstakeAllFeeCvc, UnstakeAllManagerCvc } from '../../chain/merchant/frontend'
import { MerchantErrorMessage, ReadMerchantReceipt } from '../../chain/merchant/frontendResults'
import { useEthers } from '../../ethers'
import { BN, EtherToWei, WeiToUnits, GetGlobalSigner, ZERO_ADDRESS } from '../../ethers/e'
import { USDT, USD, USD2, CVCD, CVC } from '../../chain/contract'
import { CHAIN_ID, MERCHANT_MANAGER, MERCHANT_SWAP } from '../../chain/merchant/network'
import { useBalance } from '../../context/balance'
import { getUrlParams, isETHAddress } from '../../utils'
import useInput from '../useInput'
import useButtonText from '../useButtonText'
import useToast from '../useToast'
import { sharedMerchantRead } from './sharedRead'

// 各查询 hook 保持独立状态；成功交易后统一通知当前账户的已挂载查询刷新。
const refreshListeners = new Set()
function useMerchantRefreshListener(account, refresh) {
    useEffect(() => {
        const entry = { account, refresh }
        refreshListeners.add(entry)
        return () => { refreshListeners.delete(entry) }
    }, [account, refresh])
}
function refreshMerchantQueries(account) {
    for (const entry of refreshListeners) {
        if (entry.account === account) entry.refresh()
    }
}

/**
 * loading 仅表示首次加载；fetching 表示包含后台刷新在内的请求状态。
 * 同账户后台刷新保留上次成功数据，交易状态由各操作独立管理。
 * 切换账户立即隐藏旧数据；refresh 仅触发刷新，不返回查询 Promise。
 */
function useMerchantQuery(query, errorCode, errorMessage, enabled = true) {
    const { account, getBlockNumber } = useEthers()
    const blockNumber = getBlockNumber()
    const [refreshVersion, setRefreshVersion] = useState(0)
    const lastRefresh = useRef(0)
    const provider = GetGlobalSigner()
    const [state, setState] = useState({
        account,
        data: query.Type,
        loaded: false,
        loading: Boolean(account),
        error: null,
        refreshError: null
    })

    const refresh = useCallback(() => {
        setRefreshVersion(version => version + 1)
    }, [])
    useMerchantRefreshListener(account, refresh)

    useEffect(() => {
        let cancelled = false

        if (!account || !enabled) {
            setState({ account, data: query.Type, loaded: false, loading: false, error: null, refreshError: null })
            return
        }

        setState(previous => ({
            account,
            data: previous.account === account ? previous.data : query.Type,
            loaded: previous.account === account && previous.loaded,
            loading: true,
            // Keep a read failure visible until recovery; do not flash stale actions on every retry.
            error: previous.account === account ? previous.error : null,
            refreshError: previous.account === account ? previous.refreshError : null
        }))

        const force = lastRefresh.current !== refreshVersion
        lastRefresh.current = refreshVersion
        async function load() {
            try {
                const data = await sharedMerchantRead(query, account, provider, force)
                if (!cancelled) {
                    setState({ account, data, loaded: true, loading: false, error: null, refreshError: null })
                }
            } catch (error) {
                if (!cancelled) {
                    const failure = { code: errorCode, message: MerchantErrorMessage(error, errorMessage) }
                    const invalidContext = ['MERCHANT_NETWORK_MISMATCH', 'MERCHANT_ACCOUNT_CHANGED',
                        'MIGRATION_DEPLOYMENT_MISMATCH', 'CVCD_EXIT_DISABLED'].includes(error?.code)
                    setState(previous => {
                        const keepData = previous.account === account && previous.loaded && !invalidContext
                        return { ...previous, loading: false,
                            error: keepData ? null : failure,
                            refreshError: keepData ? failure : null }
                    })
                }
            }
        }

        load()
        return () => {
            cancelled = true
        }
    }, [account, blockNumber, refreshVersion, query, errorCode, errorMessage, enabled, provider])

    // effect 执行前的首次渲染也不能暴露其他账户的数据。
    const current = state.account === account
    return {
        data: current ? state.data : query.Type,
        hasData: current && state.loaded,
        loading: current ? state.loading && !state.loaded : Boolean(account),
        fetching: current ? state.loading : Boolean(account),
        error: current ? state.error : null,
        refreshError: current ? state.refreshError : null,
        refresh
    }
}

export function useMerchantData(enabled = true) {
    return useMerchantQuery(
        MerchantData,
        'MERCHANT_DATA_FAILED',
        'Unable to load merchant data. Please retry.',
        enabled
    )
}

export function useMerchantSwapData() {
    return useMerchantQuery(
        MerchantSwapData,
        'MERCHANT_SWAP_DATA_FAILED',
        'Unable to load merchant swap data. Please retry.'
    )
}

export function useMerchantStakeData() {
    return useMerchantQuery(
        MerchantStakeData,
        'MERCHANT_STAKE_DATA_FAILED',
        'Unable to load merchant stake data. Please retry.'
    )
}

export function useMerchantFeeData() {
    return useMerchantQuery(MerchantFeeData, 'MERCHANT_FEE_DATA_FAILED', 'Unable to load merchant fee data. Please retry.')
}

const UINT256_LIMIT = BN(2).pow(256)

function parseQuoteAmount(value) {
    if (typeof value !== 'string') throw new Error('Invalid amount')
    const text = value.trim() || '0'
    if (!/^(?:\d+(?:\.\d{0,18})?|\.\d{1,18})$/.test(text)) throw new Error('Invalid amount')
    const amount = EtherToWei(text, 18)
    if (amount.gte(UINT256_LIMIT)) throw new Error('Invalid amount')
    return amount
}

// 输入变化只进行本地整数运算；身份和额度独立按账户/区块刷新。
function localQuoteState(values, source, calculate) {
    const empty = { quote: null, displayQuote: null, loading: false, error: null, refresh: source.refresh }
    let amounts
    try {
        amounts = values.map(parseQuoteAmount)
    } catch {
        return { ...empty, error: { code: 'INVALID_QUOTE_AMOUNT', message: 'Enter valid non-negative amounts with at most 18 decimal places.' } }
    }
    if (!amounts.some(amount => amount.gt(0))) return empty
    if (source.error) return { ...empty, error: source.error }
    if (!source.hasData) return { ...empty, loading: source.loading }
    try {
        const quote = calculate(amounts)
        return { ...empty, quote, displayQuote: quote }
    } catch (error) {
        return { ...empty, error: { code: 'INVALID_LOCAL_QUOTE', message: error.message } }
    }
}

export function useQuoteBuyUsd(amountUsdt = '', amountCvcd = '', amountUsd2 = '') {
    const merchant = useMerchantData()
    return localQuoteState([amountUsdt, amountCvcd, amountUsd2], merchant, ([usdt, cvcd, usd2]) => {
        const cap = usdt.mul(2).div(5)
        if (usdt.isZero() || !usd2.isZero() || (!cvcd.isZero() && !cvcd.eq(cap))) {
            throw new Error('Use USDT alone or USDT with floor(USDT * 2 / 5) CVCD, without USD2.')
        }
        const cvcdUsed = cvcd
        const multiplier = merchant.data.identities.merchant.active ? 5 : 1
        const usdReceived = usdt.add(cvcdUsed).mul(multiplier)
        if (usdReceived.gte(UINT256_LIMIT)) throw new Error('The quoted amount exceeds the supported range.')
        return { cvcdUsed: WeiToUnits(cvcdUsed), usdReceived: WeiToUnits(usdReceived) }
    })
}

// 使用最近一次读取的免质押额度，超出部分的 10% CVC 向上取整到 wei。
// 固定比例金额是前端预览；实际费用舍入以交易前链上校验和回执为准。
export function useQuoteRedeemUsd2(amount = '') {
    const swap = useMerchantSwapData()
    return localQuoteState([amount], swap, ([gross]) => {
        const { freeAvailable, totalAvailable } = swap.data.withdrawalQuota
        if (gross.gt(EtherToWei(totalAvailable))) throw new Error('The withdrawal quota is insufficient.')
        const free = EtherToWei(freeAvailable)
        const freeUsed = gross.lt(free) ? gross : free
        const cvcRequired = gross.sub(freeUsed).add(9).div(10)
        return {
            freeUsed: WeiToUnits(freeUsed), cvcRequired: WeiToUnits(cvcRequired),
            feeUsdt: WeiToUnits(gross.mul(15).div(100)),
            netUsdt: WeiToUnits(gross.mul(50).div(100)),
            reinvestedUsd2: WeiToUnits(gross.mul(35).div(100))
        }
    })
}

// 身份激活、USD 入金、兑换共用同一套推荐人规则。
function useMerchantSponsor() {
    const { account } = useEthers()
    const merchant = useMerchantData()
    const input = useInput('')
    const setInput = input.onChange
    useEffect(() => {
        const { r } = getUrlParams()
        setInput(isETHAddress(r) ? r : '')
    }, [account, setInput])
    const bound = isETHAddress(merchant.data.sponsor) && merchant.data.sponsor.toLowerCase() !== ZERO_ADDRESS
    async function resolve(fail) {
        const existing = await MerchantSponsor(account)
        if (isETHAddress(existing) && existing.toLowerCase() !== ZERO_ADDRESS) return existing
        const candidate = input.value.trim()
        if (!isETHAddress(candidate) || candidate.toLowerCase() === ZERO_ADDRESS || candidate.toLowerCase() === account.toLowerCase()) {
            fail('Enter a valid sponsor other than your own address.')
        }
        const parent = await MerchantSponsor(candidate)
        if (!isETHAddress(parent) || parent.toLowerCase() === ZERO_ADDRESS) fail('Sponsor must already be bound.')
        return candidate
    }
    return {
        input: { ...input, value: bound ? merchant.data.sponsor : input.value, readOnly: bound },
        // 链上绑定变化允许重新解析；用户修改输入则中止正在检查的交易。
        inputKey: input.value,
        bound, resolve, refresh: merchant.refresh
    }
}

const IDENTITY_PRICES = { 1: '1', 2: '1', 3: '5' }

/**
 * onSuccess 可用于刷新页面其他数据；其失败不改变已确认交易的成功结果。
 * 返回按钮点击结果为 boolean；授权已在 EnsureMerchantAllowance 中等待确认。
 */
function useMerchantRouterAction({ identity, amountInput, onSuccess }) {
    const isDeposit = Boolean(amountInput)
    const amountValue = amountInput?.value
    const token = isDeposit ? USD : USDT
    const { t } = useTranslation()
    const { account } = useEthers()
    const { balances, refresh: refreshBalances } = useBalance()
    const sponsorState = useMerchantSponsor()
    const sponsorInput = sponsorState.input
    const { button, loadingButton, initButton } = useButtonText(t(isDeposit ? 'Deposit USD' : 'Buy Identity'))
    const { open } = useToast()
    const busy = useRef(false)
    const mounted = useRef(true)
    const current = useRef({ account, identity, amountValue })
    current.current = { account, identity, amountValue }
    const validIdentity = [1, 2, 3].includes(identity)
    const price = validIdentity ? IDENTITY_PRICES[identity] : null
    const bound = sponsorState.bound

    useEffect(() => {
        mounted.current = true
        return () => { mounted.current = false }
    }, [])

    async function onClick() {
        if (busy.current) return false
        busy.current = true
        loadingButton(t('Checking'))
        // 只展示本地确定的提示，不展示原始 RPC 异常。
        let failureMessage = isDeposit ? 'Unable to deposit USD. Please retry.' : 'Unable to buy merchant identity. Please retry.'
        const fail = message => { failureMessage = message; throw new Error('Merchant action check failed') }
        try {
            if (!isDeposit && !validIdentity) fail('Select a supported merchant identity.')
            const signer = GetGlobalSigner()
            if (!signer || typeof signer.getAddress !== 'function' || typeof signer.getChainId !== 'function') {
                fail('Connect a wallet to continue.')
            }
            async function checkWallet() {
                if (!mounted.current || current.current.account !== account || current.current.identity !== identity || current.current.amountValue !== amountValue) {
                    fail('Account or transaction input changed. Please retry.')
                }
                const [owner, chainId] = await Promise.all([signer.getAddress(), signer.getChainId()])
                if (!mounted.current || current.current.account !== account || current.current.identity !== identity || current.current.amountValue !== amountValue) {
                    fail('Account or transaction input changed. Please retry.')
                }
                if (!isETHAddress(account) || owner.toLowerCase() !== account.toLowerCase() || GetGlobalSigner() !== signer) {
                    fail('Displayed account does not match the connected wallet.')
                }
                if (chainId !== CHAIN_ID) fail('Select the configured merchant network.')
            }
            await checkWallet()
            let required
            if (isDeposit) {
                try { required = parseQuoteAmount(amountValue) } catch {
                    fail('Enter a valid USD amount with at most 18 decimal places.')
                }
                if (required.isZero()) fail('Deposit amount must be greater than zero.')
            } else {
                required = EtherToWei(price, token[1])
            }
            if (balances?.[token[2]] === undefined) fail(isDeposit ? 'USD balance is unavailable. Please refresh.' : 'USDT balance is unavailable. Please refresh.')
            if (EtherToWei(balances[token[2]], token[1]).lt(required)) fail(isDeposit ? 'Insufficient USD balance.' : 'Insufficient USDT balance.')

            await sponsorState.resolve(fail)
            await checkWallet()
            loadingButton(t(isDeposit ? 'Approve USD' : 'Approve USDT'))
            await EnsureMerchantAllowance(token, MERCHANT_MANAGER, required)
            // 授权可能花费多个区块，重新读取绑定关系并检查钱包。
            await checkWallet()
            const sponsor = await sponsorState.resolve(fail)
            await checkWallet()
            loadingButton(t(isDeposit ? 'Confirm Deposit' : 'Confirm Purchase'))
            const tx = isDeposit ? await DepositUsd(required, sponsor) : await BuyMerchantIdentity(identity, sponsor)
            if (mounted.current) loadingButton(t('Pending'))
            const receipt = await tx.wait()
            if (!receipt || receipt.status !== 1) fail(isDeposit ? 'USD deposit was not successful.' : 'Identity purchase was not successful.')
            if (mounted.current && current.current.account === account) {
                if (isDeposit && current.current.amountValue === amountValue) amountInput.onChange('')
                refreshMerchantQueries(account)
                refreshBalances?.()
                open(t('Success'))
                if (onSuccess) {
                    try { await onSuccess(receipt) } catch {
                        open(t(isDeposit ? 'Deposit confirmed. Please refresh the page data.' : 'Purchase confirmed. Please refresh the page data.'), 'warning')
                    }
                }
            }
            return true
        } catch (error) {
            if (mounted.current) {
                open(t(MerchantErrorMessage(error, failureMessage)), 'error')
            }
            return false
        } finally {
            busy.current = false
            if (mounted.current) initButton()
        }
    }

    return {
        button: { ...button, onClick, disabled: button.loading || (!isDeposit && !validIdentity) || !isETHAddress(account) },
        sponsorInput: { ...sponsorInput, readOnly: bound || button.loading },
        sponsorBound: bound,
        price,
        refresh: sponsorState.refresh
    }
}

export function useBuyMerchantIdentity(identity, onSuccess) {
    return useMerchantRouterAction({ identity, onSuccess })
}

export function useDepositUsd(onSuccess) {
    const amountInput = useInput('', { type: 'number' })
    const { button, sponsorInput, sponsorBound, refresh } = useMerchantRouterAction({ amountInput, onSuccess })
    return {
        button,
        sponsorInput,
        sponsorBound,
        amountInput: { ...amountInput, readOnly: button.loading },
        refresh
    }
}

/**
 * 返回可直接传给 Button 的属性，onClick 返回是否确认成功。
 * 可领取额只取仓位 availableAmount，不能使用奖励账本 pending 或钱包余额。
 */
export function useWithdrawUsd2(onSuccess, source) {
    const { t } = useTranslation()
    const { account } = useEthers()
    // Use the displayed position snapshot; do not launch a second block query.
    const ownQuery = useMerchantData(!source)
    const merchant = source || ownQuery
    const { refresh: refreshBalances } = useBalance()
    const { button, loadingButton, initButton } = useButtonText(t('Claim'))
    const { open } = useToast()
    const busy = useRef(false)
    const mounted = useRef(true)
    const currentAccount = useRef(account)
    currentAccount.current = account
    useEffect(() => {
        mounted.current = true
        return () => { mounted.current = false }
    }, [])

    async function onClick() {
        if (busy.current) return false
        busy.current = true
        loadingButton(t('Checking'))
        let failureMessage = 'Unable to withdraw USD2. Please retry.'
        const fail = message => { failureMessage = message; throw new Error('USD2 withdrawal check failed') }
        try {
            const signer = GetGlobalSigner()
            if (!signer || typeof signer.getAddress !== 'function' || typeof signer.getChainId !== 'function') {
                fail('Connect a wallet to continue.')
            }
            async function checkWallet() {
                const [owner, chainId] = await Promise.all([signer.getAddress(), signer.getChainId()])
                if (!mounted.current || currentAccount.current !== account) fail('Account changed. Please retry.')
                if (!isETHAddress(account) || owner.toLowerCase() !== account.toLowerCase() || GetGlobalSigner() !== signer) {
                    fail('Displayed account does not match the connected wallet.')
                }
                if (chainId !== CHAIN_ID) fail('Select the configured merchant network.')
            }
            await checkWallet()
            const latest = await MerchantData(account)
            await checkWallet()
            if (EtherToWei(latest.position.availableAmount).lte(0)) {
                refreshMerchantQueries(account)
                fail('No released USD2 is available to claim.')
            }
            loadingButton(t('Claim'))
            const tx = await WithdrawUsd2()
            if (mounted.current) loadingButton(t('Pending'))
            const receipt = await tx.wait()
            if (!receipt || receipt.status !== 1) fail('USD2 withdrawal was not successful.')
            if (mounted.current && currentAccount.current === account) {
                refreshMerchantQueries(account)
                refreshBalances?.()
                open(t('Success'))
                if (onSuccess) {
                    try { await onSuccess(receipt) } catch {
                        if (mounted.current && currentAccount.current === account) {
                            open(t('Withdrawal confirmed. Please refresh the page data.'), 'warning')
                        }
                    }
                }
            }
            return true
        } catch (error) {
            if (mounted.current && currentAccount.current === account) {
                open(t(MerchantErrorMessage(error, failureMessage)), 'error')
            }
            return false
        } finally {
            busy.current = false
            if (mounted.current) initButton()
        }
    }

    const available = EtherToWei(merchant.data.position.availableAmount).gt(0)
    return {
        ...button,
        onClick,
        // Background block refreshes retain valid account data; only the initial
        // read (or an account switch) must block the button while data loads.
        disabled: button.loading || !merchant.hasData || Boolean(merchant.error) || !isETHAddress(account) || !available
    }
}

/**
 * 兑换/质押交易共用流程。inputKey 只包含用户可修改的参数，区块更新不取消操作。
 * check 在每次授权及发送交易前复核账户、网络与参数；success 仅认 status=1。
 */
function useMerchantTransaction({ label, inputKey, execute, onSuccess, reset }) {
    const { t } = useTranslation()
    const { account } = useEthers()
    const { balances, refresh: refreshBalances } = useBalance()
    const { open } = useToast()
    const { button, loadingButton, initButton } = useButtonText(t(label))
    const [result, setResult] = useState(null)
    const [failure, setFailure] = useState(null)
    const busy = useRef(false)
    const mounted = useRef(true)
    const current = useRef({ account, inputKey, balances })
    current.current = { account, inputKey, balances }
    const refresh = useCallback(() => refreshMerchantQueries(account), [account])
    useEffect(() => {
        mounted.current = true
        return () => { mounted.current = false }
    }, [])

    async function onClick() {
        if (busy.current) return false
        busy.current = true
        setFailure(null)
        setResult(null)
        loadingButton(t('Checking'))
        let failureMessage = 'Merchant transaction failed. Please retry.'
        const fail = message => { failureMessage = message; throw new Error('Merchant transaction check failed') }
        try {
            const signer = GetGlobalSigner()
            if (!signer || typeof signer.getAddress !== 'function' || typeof signer.getChainId !== 'function') {
                fail('Connect a wallet to continue.')
            }
            const unchanged = () => mounted.current && current.current.account === account && current.current.inputKey === inputKey
            async function check() {
                if (!unchanged()) fail('Account or transaction input changed. Please retry.')
                const [owner, chainId] = await Promise.all([signer.getAddress(), signer.getChainId()])
                if (!unchanged()) fail('Account or transaction input changed. Please retry.')
                if (!isETHAddress(account) || owner.toLowerCase() !== account.toLowerCase() || GetGlobalSigner() !== signer) {
                    fail('Displayed account does not match the connected wallet.')
                }
                if (chainId !== CHAIN_ID) fail('Select the configured merchant network.')
            }
            function checkBalances(requirements) {
                for (const [token, amount] of requirements) {
                    if (amount.isZero()) continue
                    const balance = current.current.balances?.[token[2]]
                    if (typeof balance !== 'string') fail(`${token[2]} balance is unavailable. Please refresh.`)
                    if (EtherToWei(balance, token[1]).lt(amount)) fail(`Insufficient ${token[2]} balance.`)
                }
            }
            async function approve(requirements, spender) {
                checkBalances(requirements)
                for (const [token, amount] of requirements) {
                    if (amount.isZero()) continue
                    await check()
                    loadingButton(t(`Approve ${token[2]}`))
                    await EnsureMerchantAllowance(token, spender, amount)
                }
                await check()
            }
            function parse(value, positive = true) {
                let amount
                try { amount = parseQuoteAmount(value) } catch { fail('Enter valid amounts with at most 18 decimal places.') }
                if (positive && amount.isZero()) fail('Amount must be greater than zero.')
                return amount
            }
            await check()
            const tx = await execute({ account, check, approve, checkBalances, fail, parse,
                stage: text => { if (mounted.current) loadingButton(t(text)) } })
            if (mounted.current) loadingButton(t('Pending'))
            const receipt = await tx.wait()
            if (!receipt || receipt.status !== 1) fail('Merchant transaction was not successful.')
            const events = ReadMerchantReceipt(receipt, account)
            if (mounted.current && current.current.account === account) {
                setResult({ account, receipt, events })
                if (unchanged() && reset) reset()
                refreshMerchantQueries(account)
                refreshBalances?.()
                open(t('Success'))
                if (onSuccess) {
                    try { await onSuccess(receipt, events) } catch {
                        if (mounted.current && current.current.account === account) {
                            open(t('Transaction confirmed. Please refresh the page data.'), 'warning')
                        }
                    }
                }
            }
            return true
        } catch (error) {
            if (mounted.current && current.current.account === account) {
                const message = MerchantErrorMessage(error, failureMessage)
                setFailure({ account, inputKey, message })
                refreshMerchantQueries(account)
                open(t(message), 'error')
            }
            return false
        } finally {
            busy.current = false
            if (mounted.current) initButton()
        }
    }

    return {
        button: { ...button, onClick, disabled: button.loading || !isETHAddress(account) },
        error: failure?.account === account && failure.inputKey === inputKey ? failure.message : null,
        lastResult: result?.account === account ? result : null,
        refresh
    }
}

function buyQuoteAcceptable(actual, expected) {
    return expected && EtherToWei(actual.usdReceived).gte(EtherToWei(expected.usdReceived)) &&
        EtherToWei(actual.cvcdUsed).lte(EtherToWei(expected.cvcdUsed))
}

function redeemQuoteAcceptable(actual, expected) {
    return expected && EtherToWei(actual.cvcRequired).lte(EtherToWei(expected.cvcRequired)) &&
        EtherToWei(actual.netUsdt).gte(EtherToWei(expected.netUsdt)) &&
        EtherToWei(actual.reinvestedUsd2).gte(EtherToWei(expected.reinvestedUsd2)) &&
        EtherToWei(actual.feeUsdt).lte(EtherToWei(expected.feeUsdt))
}

export function useBuyUsd(onSuccess, mode = 'single') {
    const usdtInput = useInput('', { type: 'number' })
    const sponsor = useMerchantSponsor()
    const action = useBuyUsdWithInputs(usdtInput, mode, sponsor, onSuccess)
    return { ...action, sponsorBound: sponsor.bound,
        sponsorInput: { ...sponsor.input, readOnly: sponsor.bound || action.button.loading } }
}

function useBuyUsdWithInputs(usdtInput, mode, sponsor, onSuccess) {
    let cvcd = ''
    try { cvcd = WeiToUnits(mode === 'mixed' ? parseQuoteAmount(usdtInput.value).mul(2).div(5) : BN(0)) } catch { /* 报价校验输入 */ }
    const quoteState = useQuoteBuyUsd(usdtInput.value, cvcd, '0')
    const transaction = useMerchantTransaction({
        label: 'Buy USD',
        inputKey: JSON.stringify([usdtInput.value, mode, sponsor.inputKey]),
        onSuccess,
        reset: () => usdtInput.onChange(''),
        execute: async ({ account, check, approve, checkBalances, fail, parse, stage }) => {
            if (!['single', 'mixed'].includes(mode)) fail('Unsupported merchant action.')
            const usdt = parse(usdtInput.value)
            const amounts = [usdt, mode === 'mixed' ? usdt.mul(2).div(5) : BN(0), BN(0)]
            await sponsor.resolve(fail)
            await check()
            if (!quoteState.quote) fail('Wait for a valid quote before confirming.')
            const quoted = await QuoteBuyUsd(account, ...amounts)
            if (!buyQuoteAcceptable(quoted, quoteState.quote)) fail('Quote changed. Review the refreshed quote and confirm again.')
            if (EtherToWei(quoted.usdReceived).lte(0)) fail('The quoted USD output must be greater than zero.')
            const requirements = [[USDT, amounts[0]], [CVCD, amounts[1]]]
            await approve(requirements, MERCHANT_SWAP)
            const latest = await QuoteBuyUsd(account, ...amounts)
            if (!buyQuoteAcceptable(latest, quoted)) fail('Quote changed. Review the refreshed quote and confirm again.')
            checkBalances(requirements)
            const resolvedSponsor = await sponsor.resolve(fail)
            await check()
            stage('Confirm Purchase')
            return BuyUsd(...amounts, resolvedSponsor, mode === 'mixed' ? 6 : 5)
        }
    })
    return {
        ...transaction,
        button: { ...transaction.button, disabled: transaction.button.disabled || !quoteState.quote || quoteState.loading },
        usdtInput: { ...usdtInput, readOnly: transaction.button.loading },
        cvcdInput: { value: cvcd, readOnly: true },
        quote: quoteState.quote, displayQuote: quoteState.displayQuote, quoteLoading: quoteState.loading, quoteError: quoteState.error
    }
}

// 两个按钮共用 USDT 输入和 sponsor；CVCD 固定按 wei 向下取整。
export function useGetUsd() {
    const amountInput = useInput('', { type: 'number' })
    const sponsor = useMerchantSponsor()
    const busy = useRef(false)
    const [lastPurchase, setLastPurchase] = useState(null)
    const { account } = useEthers()
    const onSuccess = (receipt, events) => {
        const purchase = events.find(event => event.source === 'swap' && event.name === 'UsdPurchased')
        setLastPurchase(purchase ? { account, amount: purchase.values.usdReceived } : null)
    }
    let requiredUsdt = null
    try { requiredUsdt = WeiToUnits(parseQuoteAmount(amountInput.value)) } catch { /* 报价校验输入 */ }
    const single = useBuyUsdWithInputs(amountInput, 'single', sponsor, onSuccess)
    const mixed = useBuyUsdWithInputs(amountInput, 'mixed', sponsor, onSuccess)
    const loading = single.button.loading || mixed.button.loading
    const wrapButton = action => ({
        ...action.button,
        disabled: action.button.disabled || loading,
        onClick: async () => {
            if (busy.current || action.button.disabled) return false
            busy.current = true
            setLastPurchase(null)
            try { return await action.button.onClick() } finally { busy.current = false }
        }
    })
    return {
        amountInput: { ...amountInput, readOnly: loading,
            onChange: value => { if (!busy.current) amountInput.onChange(value) } },
        sponsorBound: sponsor.bound,
        sponsorInput: { ...sponsor.input, readOnly: sponsor.bound || loading,
            onChange: value => { if (!busy.current && !sponsor.bound) sponsor.input.onChange(value) } },
        receivedUsd: lastPurchase?.account === account ? lastPurchase.amount : null,
        requiredUsdt,
        single: { ...single, button: wrapButton(single) },
        mixed: { ...mixed, button: wrapButton(mixed) },
        loading
    }
}

export function useRedeemUsd2(onSuccess) {
    const amountInput = useInput('', { type: 'number' })
    const quoteState = useQuoteRedeemUsd2(amountInput.value)
    const transaction = useMerchantTransaction({
        label: 'Swap to USDT', inputKey: amountInput.value, onSuccess,
        reset: () => amountInput.onChange(''),
        execute: async ({ account, check, approve, checkBalances, fail, parse, stage }) => {
            const amount = parse(amountInput.value)
            if (!quoteState.quote) fail('Wait for a valid quote before confirming.')
            const quoted = await QuoteRedeemUsd2(account, amount)
            // 本地仅展示 CVC 需求；手续费舍入使用链上结果，再于授权后复核。
            if (EtherToWei(quoted.cvcRequired).gt(EtherToWei(quoteState.quote.cvcRequired))) fail('Quote changed. Review the refreshed quote and confirm again.')
            const requirements = quote => [[USD2, amount], [CVC, EtherToWei(quote.cvcRequired)]]
            await approve(requirements(quoted), MERCHANT_SWAP)
            const latest = await QuoteRedeemUsd2(account, amount)
            if (!redeemQuoteAcceptable(latest, quoted)) fail('Quote changed. Review the refreshed quote and confirm again.')
            checkBalances(requirements(latest))
            await check()
            stage('Confirm Swap')
            return RedeemUsd2(amount)
        }
    })
    return {
        ...transaction,
        button: { ...transaction.button, disabled: transaction.button.disabled || !quoteState.quote || quoteState.loading },
        amountInput: { ...amountInput, readOnly: transaction.button.loading },
        quote: quoteState.quote, displayQuote: quoteState.displayQuote, quoteLoading: quoteState.loading, quoteError: quoteState.error
    }
}

function useCvcStake(isFee, onSuccess) {
    const amountInput = useInput('', { type: 'number' })
    const transaction = useMerchantTransaction({
        label: isFee ? 'Stake CVC & Burn CVCD' : 'Stake CVC',
        inputKey: amountInput.value, onSuccess,
        reset: () => amountInput.onChange(''),
        execute: async ({ check, approve, checkBalances, parse, stage }) => {
            const amount = parse(amountInput.value)
            const requirements = isFee ? [[CVC, amount], [CVCD, amount]] : [[CVC, amount]]
            await approve(requirements, isFee ? MERCHANT_SWAP : MERCHANT_MANAGER)
            checkBalances(requirements)
            await check()
            stage(isFee ? 'Confirm Stake & Burn' : 'Confirm Stake')
            return isFee ? StakeFeeCvc(amount) : StakeManagerCvc(amount)
        }
    })
    return { ...transaction, amountInput: { ...amountInput, readOnly: transaction.button.loading } }
}

export function useStakeFeeCvc(onSuccess) {
    return useCvcStake(true, onSuccess)
}

export function useStakeManagerCvc(onSuccess) {
    return useCvcStake(false, onSuccess)
}

const UNSTAKE_ACTIONS = { swap: UnstakeSwapCvc, swapFee: UnstakeFeeCvc, manager: UnstakeManagerCvc }

// 返回按钮属性。页面用 order.claimable 控制展示，点击时仍从链上复核订单状态。
export function useUnstakeCvc(source, orderId, onSuccess) {
    const validSource = Object.prototype.hasOwnProperty.call(UNSTAKE_ACTIONS, source)
    let id = null
    try {
        const value = BN(orderId)
        if (value.gte(0) && value.lt(UINT256_LIMIT)) id = value.toString()
    } catch { /* 缺失或非法 id 禁止调用任何领取重载 */ }
    const transaction = useMerchantTransaction({
        label: 'Claim CVC', inputKey: `${source}:${id}`, onSuccess,
        execute: async ({ account, check, fail, stage }) => {
            if (!validSource || id === null) fail('Select a valid stake source and order.')
            const latest = await MerchantStakeData(account)
            const order = latest.orders.find(item => item.source === source && String(item.id) === id)
            if (!order || !order.claimable) fail('This CVC order is not available to claim. Refresh the orders.')
            await check()
            stage('Claim')
            return UNSTAKE_ACTIONS[source](id)
        }
    })
    return { ...transaction.button, disabled: transaction.button.disabled || !validSource || id === null }
}


// 一个按钮按来源依次领取；每类调用无参重载，合约负责筛选到期订单。
// 已确认的前序交易不会因后续拒绝而回滚，逐笔刷新余额与订单。
export function useClaimMaturedCvc() {
    const { t } = useTranslation()
    const { account } = useEthers()
    const { refresh: refreshBalances } = useBalance()
    const { open } = useToast()
    const { button, loadingButton, initButton } = useButtonText(t('Claim'))
    const busy = useRef(false)
    const mounted = useRef(true)
    const currentAccount = useRef(account)
    currentAccount.current = account
    useEffect(() => {
        mounted.current = true
        return () => { mounted.current = false }
    }, [])

    async function onClick() {
        if (busy.current) return false
        busy.current = true
        loadingButton(t('Checking'))
        let confirmed = 0
        let failureMessage = 'Unable to claim CVC. Please retry.'
        const fail = message => { failureMessage = message; throw new Error('CVC claim check failed') }
        const current = () => mounted.current && currentAccount.current === account
        try {
            const signer = GetGlobalSigner()
            if (!signer?.getAddress || !signer?.getChainId) fail('Connect a wallet to continue.')
            async function check() {
                if (!current()) fail('Account changed. Please retry.')
                const [owner, chainId] = await Promise.all([signer.getAddress(), signer.getChainId()])
                if (!current()) fail('Account changed. Please retry.')
                if (!isETHAddress(account) || owner.toLowerCase() !== account.toLowerCase() || GetGlobalSigner() !== signer) {
                    fail('Displayed account does not match the connected wallet.')
                }
                if (chainId !== CHAIN_ID) fail('Select the configured merchant network.')
            }
            const actions = [
                ['swap', UnstakeAllSwapCvc], ['swapFee', UnstakeAllFeeCvc], ['manager', UnstakeAllManagerCvc]
            ]
            for (const [source, claim] of actions) {
                await check()
                const latest = await MerchantStakeData(account)
                await check()
                if (!latest.orders.some(order => order.source === source && order.claimable)) continue
                loadingButton(t('Claim'))
                const tx = await claim()
                if (current()) loadingButton(t('Pending'))
                const receipt = await tx.wait()
                if (!receipt || receipt.status !== 1) fail('CVC claim was not successful.')
                confirmed += 1
                if (current()) {
                    refreshMerchantQueries(account)
                    refreshBalances?.()
                }
            }
            if (!confirmed) fail('No matured CVC stake is available to claim.')
            if (current()) open(t('Success'))
            return true
        } catch (error) {
            if (current()) {
                refreshMerchantQueries(account)
                const message = MerchantErrorMessage(error, failureMessage)
                open(confirmed ? `${t('Some CVC claims are confirmed.')} ${t(message)}` : t(message), 'error')
            }
            return false
        } finally {
            busy.current = false
            if (mounted.current) initButton()
        }
    }
    return { ...button, onClick, disabled: button.loading || !isETHAddress(account) }
}

export function useClaimInitialIdentities() {
    const eligibility = useMerchantQuery(InitialIdentityData, 'INITIAL_IDENTITY_DATA_FAILED', 'Unable to load free activation eligibility. Please retry.')
    const transaction = useMerchantTransaction({
        label: 'Activate', inputKey: 'initial-identities',
        execute: async ({ account, check, fail, stage }) => {
            const latest = await InitialIdentityData(account)
            await check()
            if (!latest.eligible) fail('Only Node users and above can claim free identities.')
            if (!latest.memberAvailable && !latest.vipAvailable) fail('No first-time free identities remain. Use paid renewal.')
            stage('Confirm Activation')
            return ClaimInitialIdentities()
        }
    })
    const claimed = transaction.lastResult?.events.find(event => event.source === 'manager' && event.name === 'InitialIdentitiesClaimed')
    return {
        ...transaction,
        available: eligibility.hasData && !eligibility.error && eligibility.data.eligible &&
            (eligibility.data.memberAvailable || eligibility.data.vipAvailable),
        memberAvailable: eligibility.data.memberAvailable,
        vipAvailable: eligibility.data.vipAvailable,
        claimed: claimed?.values ?? null,
        button: { ...transaction.button, disabled: transaction.button.disabled || !eligibility.hasData || !!eligibility.error ||
            !eligibility.data.eligible || (!eligibility.data.memberAvailable && !eligibility.data.vipAvailable) }
    }
}

// Kept as a compatibility name for existing consumers; no CVC stake or approval.
export function useStakeStairCvc(onSuccess) {
    const summary = useMerchantQuery(MerchantStairData, 'MERCHANT_STAIR_DATA_FAILED',
        'Unable to load Stair migration data. Please retry.')
    const eligible = data => data.needsSettlement || (!data.converted && EtherToWei(data.remainingPrincipal).gt(0))
    const transaction = useMerchantTransaction({
        label: summary.data.isLegacy ? 'Settle Legacy Stair' : 'Confirm Migration',
        inputKey: `stair-migration:${summary.data.isLegacy}`, onSuccess,
        execute: async ({ account, check, fail, stage }) => {
            const latest = await MerchantStairData(account)
            await check()
            if (!eligible(latest)) fail('No active Stair principal or settlement is available.')
            if (!summary.hasData || summary.error || latest.isLegacy !== summary.data.isLegacy ||
                latest.remainingPrincipal !== summary.data.remainingPrincipal) {
                fail('Stair principal changed. Review the refreshed amount and confirm again.')
            }
            stage(latest.isLegacy ? 'Settle Legacy Stair' : 'Confirm Migration')
            return latest.isLegacy ? SettleLegacyStair(check) : MigrateStair(check)
        }
    })
    const available = summary.hasData && !summary.error && eligible(summary.data)
    return { ...transaction, summary, available,
        button: { ...transaction.button, disabled: transaction.button.disabled || !available } }
}

export function useCvcdExit() {
    const summary = useMerchantQuery(CvcdExitData, 'CVCD_EXIT_DATA_FAILED', 'Unable to load exit refunds. Please retry.')
    const transaction = useMerchantTransaction({
        label: 'Claim Exit Refund', inputKey: 'cvcd-exit',
        execute: async ({ account, check, fail }) => {
            const latest = await CvcdExitData(account)
            await check()
            if (latest.claimed) fail('Exit refund has already been claimed.')
            if (EtherToWei(latest.cvc).isZero() && EtherToWei(latest.cvcd).isZero()) fail('No assets are available to refund.')
            return ClaimCvcdExit(check)
        }
    })
    const available = summary.hasData && !summary.error && !summary.data.claimed &&
        (EtherToWei(summary.data.cvc).gt(0) || EtherToWei(summary.data.cvcd).gt(0))
    return { ...transaction, summary,
        button: { ...transaction.button, disabled: transaction.button.disabled || !available } }
}
